import React, {FC} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {DefaultVideoConferences} from '../../../../../models/api/preferencesModel';
import {getWriteAccessCalendarData} from '../../../../../store/data/calendarVersions/calendarVersionsSelectors';
import {SettingsPreferenceItemInheritableProps} from '../../../settingsPreferencesList';
import {DefaultVideoConferencingDropdown} from './defaultVideoConferencingDropdown';
import {DefaultVideoConferencingSection} from './defaultVideoConferencingSection';

/*
 * Props.
 */

interface DefaultVideoConferencingProps
  extends SettingsPreferenceItemInheritableProps<DefaultVideoConferences> {
  defaultVideoConferences: DefaultVideoConferences;
}

/*
 * Style.
 */

const StyledBottomItemDiv = styled.div`
  display: grid;
  grid-template-areas: 'icon name right';
  grid-template-columns: auto 1fr auto;
  grid-column-gap: 8px;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  grid-area: icon;
`;

const StyledNameDiv = styled.div`
  grid-area: name;
  font-size: ${fontSizes.medium};
  line-height: 1;
  color: ${(p) => p.theme.greys.shade80};
  display: flex;
  align-items: center;
`;

const StyledRightDiv = styled.div`
  grid-area: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
`;

/*
 * Component.
 */

export const DefaultVideoConferencing: FC<DefaultVideoConferencingProps> = (props) => {
  const theme = useUserTheme();
  const {defaultVideoConferences, updatePreference} = props;
  const writeAccessCalendarData = useAppSelector(getWriteAccessCalendarData);

  const renderDefaultVideoConferenceItems = () =>
    writeAccessCalendarData.map(({calendar, calendarVersion}) => {
      const calendarVersionId = calendarVersion.id;
      const currentConferenceType = defaultVideoConferences[calendarVersionId];

      return (
        <StyledBottomItemDiv>
          <StyledIcon name="channelEmailSmall" color={theme.greys.shade70} />
          <StyledNameDiv>{calendar.name}</StyledNameDiv>
          <StyledRightDiv>
            <DefaultVideoConferencingDropdown
              conferenceType={currentConferenceType}
              supportedProviderConferenceTypes={calendar.supportedProviderConferenceTypes}
              onConferenceTypeChange={(conferenceType) => {
                const updatedDefaultVideoConferences = {
                  ...defaultVideoConferences,
                  [calendarVersionId]: conferenceType,
                };

                updatePreference(updatedDefaultVideoConferences);
              }}
            />
          </StyledRightDiv>
        </StyledBottomItemDiv>
      );
    });

  return (
    <DefaultVideoConferencingSection
      title={props.title}
      subtitle={props.description}
      bottom={renderDefaultVideoConferenceItems()}
    />
  );
};
